import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";

import { selectIdentity } from "../../../selectors/userSelectors";
import Field from "../../../shared/ui/Field/Field";
import TextInput from "../../../shared/ui/Field/TextInput";
import Textarea from "../../../shared/ui/Field/Textarea";
import Checkbox from "../../../shared/ui/Field/Checkbox";
import InlineError from "../../../shared/ui/Field/InlineError";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import AvatarCropEditor from "../../../shared/avatarCrop/AvatarCropEditor";
import SharedImageEditorModal from "../../../shared/imageEditorModal/SharedImageEditorModal";
import { canLocalPreview } from "../../../shared/imageFileContract";
import {
  messageFromError,
  parseDrfFieldErrors,
} from "../../../shared/api/drfErrors";

import {
  archiveCompany,
  fetchCompany,
  fetchMyCompany,
  patchCompany,
  patchCompanyMultipart,
} from "../api/companyWorkspace";
import {
  canArchiveCompany,
  canOrdinaryAdminWrite,
  isOwnerMembership,
} from "../lib/companyLabels";
import {
  createScopeGate,
  sameActorId,
  sameCompanyId,
} from "../lib/scopeSession";
import {
  FOCUS_REVALIDATION_UNRESOLVED_MESSAGE,
  openBackgroundRevalidation,
  settleRevalidationWrites,
  useCompanyFocusRevalidation,
} from "../lib/companyFocusRevalidation";
import CompanyPageHeader from "../ui/CompanyPageHeader";

const EDITABLE = [
  "name",
  "description",
  "email",
  "phone",
  "website",
  "city",
  "address",
  "facebook",
  "instagram",
  "youtube",
];

const FIELD_LABELS = {
  name: "Назва",
  description: "Опис",
  email: "Email",
  phone: "Телефон",
  website: "Вебсайт",
  city: "Місто",
  address: "Адреса",
  facebook: "Facebook",
  instagram: "Instagram",
  youtube: "YouTube",
};

const fromCompany = (company) => {
  const next = {};
  EDITABLE.forEach((key) => {
    next[key] = typeof company?.[key] === "string" ? company[key] : "";
  });
  next.is_open_to_join = Boolean(company?.is_open_to_join);
  return next;
};

function usePendingObjectUrl(file) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!file || !canLocalPreview(file)) {
      setUrl(null);
      return undefined;
    }
    const nextUrl = URL.createObjectURL(file);
    setUrl(nextUrl);
    return () => {
      URL.revokeObjectURL(nextUrl);
    };
  }, [file]);
  return url;
}

function pendingBrandingCopy(file, noun) {
  if (!file) return null;
  if (!canLocalPreview(file)) {
    return `Обрано: ${file.name || "файл"}\nПопередній перегляд недоступний у браузері.\nФайл буде завантажено після збереження.`;
  }
  return `Не збережено. Натисніть «Зберегти», щоб оновити ${noun}.`;
}

function CompanySettingsContent() {
  const { companyId } = useParams();
  const identity = useSelector(selectIdentity);
  const actorId = identity?.userId ?? null;
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);
  const saveOpRef = useRef(null);
  const archiveOpRef = useRef(null);
  const [company, setCompany] = useState(null);
  const [membership, setMembership] = useState(null);
  const [authorityActorId, setAuthorityActorId] = useState(null);
  const [values, setValues] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [logoEditor, setLogoEditor] = useState(null);
  const [bannerEditor, setBannerEditor] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [state, setState] = useState("loading");
  const stateRef = useRef(state);
  const [writesSuspended, setWritesSuspended] = useState(false);
  const writesSuspendedRef = useRef(false);
  const revalidationTokenRef = useRef(0);
  stateRef.current = state;
  writesSuspendedRef.current = writesSuspended;
  const [saving, setSaving] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const dirtyRef = useRef(false);
  const companyIdRef = useRef(companyId);
  const actorIdRef = useRef(actorId);
  const brandingSessionRef = useRef({ logo: 0, banner: 0 });
  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const logoTriggerRef = useRef(null);
  const bannerTriggerRef = useRef(null);
  const logoEditorWasOpenRef = useRef(false);
  companyIdRef.current = companyId;
  actorIdRef.current = actorId;
  const logoPreviewUrl = usePendingObjectUrl(logoFile);
  const bannerPreviewUrl = usePendingObjectUrl(bannerFile);
  const brandingEditorActive = Boolean(logoEditor || bannerEditor);

  const invalidateBrandingSessions = useCallback(() => {
    brandingSessionRef.current.logo += 1;
    brandingSessionRef.current.banner += 1;
    setLogoEditor(null);
    setBannerEditor(null);
  }, []);

  const brandingApplyIsCurrent = (scope) => {
    if (!mountedRef.current || !scope) return false;
    if (scope.target !== "logo" && scope.target !== "banner") return false;
    if (scope.generation !== brandingSessionRef.current[scope.target]) return false;
    if (!sameCompanyId(scope.companyId, companyIdRef.current)) return false;
    if (!sameActorId(scope.actorId, actorIdRef.current)) return false;
    return true;
  };

  const clearFieldError = (name) => {
    setFieldErrors((prev) => {
      if (!prev || !prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const clearPrivilegedForm = useCallback(() => {
    setValues(null);
    setBaseline(null);
    setLogoFile(null);
    setBannerFile(null);
    setFieldErrors({});
    dirtyRef.current = false;
    invalidateBrandingSessions();
  }, [invalidateBrandingSessions]);

  const releaseSave = (op) => {
    if (saveOpRef.current === op) {
      saveOpRef.current = null;
      setSaving(false);
    }
  };

  const releaseArchive = (op) => {
    if (archiveOpRef.current === op) {
      archiveOpRef.current = null;
      setArchiveBusy(false);
    }
  };

  const load = useCallback(
    ({ preserveDirty = false, background = false } = {}) => {
      if (!companyId) {
        setState("not-found");
        return Promise.resolve();
      }
      const keepMounted =
        background &&
        (stateRef.current === "ready" || stateRef.current === "archived");
      const op = scopeGate.beginRead({ companyId, actorId });
      const tokenAtStart = revalidationTokenRef.current;
      let backgroundToken = null;
      let revalidationConfirmed = false;
      if (keepMounted) {
        backgroundToken = openBackgroundRevalidation(
          revalidationTokenRef,
          writesSuspendedRef,
          setWritesSuspended,
        );
      } else {
        setState("loading");
        setFormError(null);
      }
      return Promise.all([fetchCompany(companyId), fetchMyCompany()])
        .then(([companyData, mine]) => {
          if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
            return;
          }
          const mineMatches =
            mine?.company?.id != null &&
            sameCompanyId(mine.company.id, companyId)
              ? mine.membership
              : null;
          setCompany(companyData);
          setMembership(mineMatches);
          setAuthorityActorId(actorId);
          if (!canOrdinaryAdminWrite(companyData, mineMatches)) {
            clearPrivilegedForm();
            setState(
              companyData.status === "archived" &&
                isOwnerMembership(companyData, mineMatches)
                ? "archived"
                : "denied",
            );
            return;
          }
          if (companyData.status === "archived") {
            clearPrivilegedForm();
            setState("archived");
            return;
          }
          if (preserveDirty && dirtyRef.current && values && baseline) {
            revalidationConfirmed = true;
            setFormError((current) =>
              current === FOCUS_REVALIDATION_UNRESOLVED_MESSAGE ? null : current,
            );
            setState("ready");
            return;
          }
          const next = fromCompany(companyData);
          setValues(next);
          setBaseline(next);
          setLogoFile(null);
          setBannerFile(null);
          revalidationConfirmed = true;
          setFormError((current) =>
            current === FOCUS_REVALIDATION_UNRESOLVED_MESSAGE ? null : current,
          );
          setState("ready");
        })
        .catch((err) => {
          if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
            return;
          }
          const status = err?.response?.status;
          if (keepMounted && status !== 403 && status !== 404) {
            setFormError(FOCUS_REVALIDATION_UNRESOLVED_MESSAGE);
            return;
          }
          clearPrivilegedForm();
          // Resolve denial against the actor who issued this request so a
          // finished HTTP 403 is not trapped behind awaiting-identity spinner.
          setAuthorityActorId(actorId);
          if (status === 404) setState("not-found");
          else if (status === 403) setState("denied");
          else {
            setFormError(messageFromError(err));
            setState("error");
          }
        })
        .finally(() => {
          settleRevalidationWrites({
            keepVisible: keepMounted,
            confirmed: revalidationConfirmed,
            token: backgroundToken,
            tokenAtStart,
            tokenRef: revalidationTokenRef,
            writesSuspendedRef,
            setWritesSuspended,
          });
        });
    },
    [actorId, baseline, clearPrivilegedForm, companyId, scopeGate, values],
  );

  useEffect(() => {
    const brandingSessions = brandingSessionRef.current;
    mountedRef.current = true;
    scopeGate.bindIdentity({ companyId, actorId });
    saveOpRef.current = null;
    archiveOpRef.current = null;
    setSaving(false);
    setArchiveBusy(false);
    clearPrivilegedForm();
    setCompany(null);
    setMembership(null);
    setAuthorityActorId(null);
    setFormError(null);
    load();
    return () => {
      mountedRef.current = false;
      brandingSessions.logo += 1;
      brandingSessions.banner += 1;
      scopeGate.invalidate();
      saveOpRef.current = null;
      archiveOpRef.current = null;
    };
  }, [companyId, actorId]); // eslint-disable-line react-hooks/exhaustive-deps -- identity/route remount

  useEffect(() => {
    const wasOpen = logoEditorWasOpenRef.current;
    const open = Boolean(logoEditor);
    logoEditorWasOpenRef.current = open;
    if (!wasOpen || open) return undefined;
    const el = logoTriggerRef.current;
    if (el && document.contains(el) && typeof el.focus === "function") {
      el.focus();
    }
    return undefined;
  }, [logoEditor]);

  useCompanyFocusRevalidation({
    enabled: state === "ready" || state === "archived",
    busy: saving || archiveBusy || brandingEditorActive,
    onRevalidate: () =>
      load({ preserveDirty: dirtyRef.current, background: true }),
  });

  const onChange = (name) => (event) => {
    const value =
      event.target.type === "checkbox" ? event.target.checked : event.target.value;
    dirtyRef.current = true;
    setValues((prev) => ({ ...prev, [name]: value }));
    clearFieldError(name);
  };

  const beginBrandingChoose = (target, file) => {
    brandingSessionRef.current[target] += 1;
    if (target === "logo") setLogoEditor(null);
    else setBannerEditor(null);
    if (!file) return;
    if (canLocalPreview(file)) {
      const scope = {
        generation: brandingSessionRef.current[target],
        companyId: companyIdRef.current,
        actorId: actorIdRef.current,
        target,
      };
      if (target === "logo") setLogoEditor({ file, scope });
      else setBannerEditor({ file, scope });
      return;
    }
    if (target === "logo") setLogoFile(file);
    else setBannerFile(file);
    dirtyRef.current = true;
    clearFieldError(target);
  };

  const onLogoInputChange = (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = "";
    beginBrandingChoose("logo", file);
  };

  const onBannerInputChange = (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = "";
    beginBrandingChoose("banner", file);
  };

  const onLogoApply = (file) => {
    const scope = logoEditor?.scope;
    if (!brandingApplyIsCurrent(scope)) return;
    brandingSessionRef.current.logo += 1;
    setLogoFile(file);
    dirtyRef.current = true;
    clearFieldError("logo");
    setLogoEditor(null);
  };

  const onLogoCancel = () => {
    const scope = logoEditor?.scope;
    if (!brandingApplyIsCurrent(scope)) return;
    brandingSessionRef.current.logo += 1;
    setLogoEditor(null);
  };

  const onBannerApply = (file) => {
    const scope = bannerEditor?.scope;
    if (!brandingApplyIsCurrent(scope)) return;
    brandingSessionRef.current.banner += 1;
    setBannerFile(file);
    dirtyRef.current = true;
    clearFieldError("banner");
    setBannerEditor(null);
  };

  const onBannerCancel = () => {
    const scope = bannerEditor?.scope;
    if (!brandingApplyIsCurrent(scope)) return;
    brandingSessionRef.current.banner += 1;
    setBannerEditor(null);
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (
      saveOpRef.current ||
      archiveOpRef.current ||
      writesSuspendedRef.current ||
      brandingEditorActive ||
      !values ||
      !baseline
    ) {
      return;
    }
    const patch = {};
    EDITABLE.forEach((key) => {
      if ((values[key] ?? "") !== (baseline[key] ?? "")) {
        patch[key] = values[key];
      }
    });
    if (values.is_open_to_join !== baseline.is_open_to_join) {
      patch.is_open_to_join = values.is_open_to_join;
    }
    const hasFiles = Boolean(logoFile || bannerFile);
    if (Object.keys(patch).length === 0 && !hasFiles) {
      setFormError("Немає змін для збереження.");
      return;
    }
    const op = scopeGate.beginMutation({ companyId, actorId });
    saveOpRef.current = op;
    setSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      let updated;
      if (hasFiles) {
        const fd = new FormData();
        Object.keys(patch).forEach((key) => {
          const val = patch[key];
          fd.append(key, typeof val === "boolean" ? String(val) : val ?? "");
        });
        if (logoFile) fd.append("logo", logoFile);
        if (bannerFile) fd.append("banner", bannerFile);
        updated = await patchCompanyMultipart(companyId, fd);
      } else {
        updated = await patchCompany(companyId, patch);
      }
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      setCompany(updated);
      const next = fromCompany(updated);
      setValues(next);
      setBaseline(next);
      setLogoFile(null);
      setBannerFile(null);
      dirtyRef.current = false;
    } catch (err) {
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const status = err?.response?.status;
      if (status === 403 || status === 404) {
        clearPrivilegedForm();
        setState(status === 404 ? "not-found" : "denied");
        return;
      }
      const parsed = parseDrfFieldErrors(err, [
        ...EDITABLE,
        "is_open_to_join",
        "logo",
        "banner",
      ]);
      const { _form, ...fields } = parsed;
      setFieldErrors(fields);
      setFormError(_form || messageFromError(err));
    } finally {
      releaseSave(op);
    }
  };

  const onArchive = async () => {
    if (
      archiveOpRef.current ||
      saveOpRef.current ||
      writesSuspendedRef.current ||
      brandingEditorActive ||
      !company ||
      !canArchiveCompany(company, membership)
    ) {
      return;
    }
    const confirmed = window.confirm(
      `Архівувати компанію «${company.name}»? Запрошення, заявки та звичайні зміни будуть зупинені. Це не жорстке видалення.`,
    );
    if (!confirmed) return;
    const op = scopeGate.beginMutation({ companyId, actorId });
    archiveOpRef.current = op;
    setArchiveBusy(true);
    try {
      const updated = await archiveCompany(companyId);
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      setCompany(updated);
      clearPrivilegedForm();
      setState("archived");
    } catch (err) {
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      setFormError(messageFromError(err));
    } finally {
      releaseArchive(op);
    }
  };

  const companyMatchesRoute =
    company != null && sameCompanyId(company.id, companyId);
  const actorMatches =
    authorityActorId != null && sameActorId(authorityActorId, actorId);
  const authorityMatches = companyMatchesRoute && actorMatches;
  const awaitingAuthority =
    state === "loading" ||
    ((state === "ready" || state === "archived") && !authorityMatches);

  if (awaitingAuthority) {
    return (
      <div className="feature-company-workspace__state">
        <Spinner size="lg" label="Завантаження налаштувань" />
      </div>
    );
  }

  if (state === "denied" || state === "not-found" || state === "error") {
    return (
      <div className="feature-company-workspace__state">
        <EmptyState
          icon="fa-solid fa-lock"
          title={
            state === "not-found"
              ? "Компанію не знайдено"
              : state === "error"
                ? "Помилка завантаження"
                : "Немає доступу"
          }
          description={
            formError ||
            (state === "denied"
              ? "Редагувати налаштування може лише активний власник або керівник активної компанії."
              : "Спробуйте іншу адресу або поверніться до компанії.")
          }
          size="md"
        />
        {state === "error" ? (
          <button type="button" className="action-btn" onClick={() => load()}>
            Повторити
          </button>
        ) : (
          <Link to="/companies/mine" className="action-btn">
            Моя компанія
          </Link>
        )}
      </div>
    );
  }

  if (state === "archived") {
    return (
      <section className="clients-page feature-company-workspace" id="Csection">
        <div className="container">
          <CompanyPageHeader
            title="Налаштування"
            company={company}
            membership={membership}
            companyId={companyId}
            activeNav="settings"
          />
          <EmptyState
            icon="fa-solid fa-box-archive"
            title="Компанія в архіві"
            description="Звичайне редагування профілю недоступне. Передача власності, вихід і інші підтримувані дії — у розділі «Команда»."
            size="md"
          />
          <div className="feature-company-workspace__inline-actions">
            <Link to={`/companies/${companyId}`} className="action-btn">
              До профілю
            </Link>
            <Link
              to={`/companies/${companyId}/members`}
              className="action-btn action-btn--ghost"
            >
              Команда
            </Link>
          </div>
        </div>
      </section>
    );
  }

  if (!authorityMatches || !values) {
    return (
      <div className="feature-company-workspace__state">
        <Spinner size="lg" label="Завантаження налаштувань" />
      </div>
    );
  }

  const showArchive = canArchiveCompany(company, membership);

  return (
    <section className="clients-page feature-company-workspace" id="Csection">
      <div className="container feature-company-workspace__narrow">
        <CompanyPageHeader
          title="Налаштування"
          subtitle="Оновіть профіль компанії. Зберігаються лише змінені поля."
          company={company}
          membership={membership}
          companyId={companyId}
          activeNav="settings"
          actions={
            <Link
              to={`/companies/${companyId}/contact/edit`}
              className="action-btn"
            >
              Контакти
            </Link>
          }
        />
        <form className="ui-form" onSubmit={onSubmit} noValidate>
          <div className="feature-company-workspace__brand-fields">
            <div className="feature-company-workspace__brand-field">
              <label className="ui-field__label" htmlFor="company-settings-logo">
                Логотип
              </label>
              {logoPreviewUrl || company.logo ? (
                <img
                  className="feature-company-workspace__brand-logo"
                  src={logoPreviewUrl || company.logo}
                  alt={`Логотип ${company.name}`}
                />
              ) : null}
              <input
                ref={logoInputRef}
                id="company-settings-logo"
                className="feature-company-workspace__brand-file-input"
                type="file"
                accept="image/*"
                disabled={saving || archiveBusy || writesSuspended}
                aria-invalid={fieldErrors.logo ? "true" : undefined}
                aria-describedby={
                  fieldErrors.logo ? "company-settings-logo-error" : undefined
                }
                onChange={onLogoInputChange}
              />
              <div className="feature-company-workspace__brand-actions">
                <button
                  ref={logoTriggerRef}
                  type="button"
                  className="action-btn action-btn--ghost"
                  disabled={saving || archiveBusy || writesSuspended}
                  onClick={() => logoInputRef.current?.click()}
                >
                  {logoFile || company.logo ? "Замінити логотип" : "Обрати логотип"}
                </button>
              </div>
              {logoEditor ? (
                <AvatarCropEditor
                  sourceFile={logoEditor.file}
                  title="Редагувати логотип"
                  onApply={onLogoApply}
                  onCancel={onLogoCancel}
                  disabled={saving || archiveBusy || writesSuspended}
                />
              ) : null}
              {logoFile ? (
                <p className="feature-company-workspace__brand-pending" role="status">
                  {pendingBrandingCopy(logoFile, "логотип")}
                </p>
              ) : null}
              {fieldErrors.logo ? (
                <InlineError id="company-settings-logo-error">
                  {fieldErrors.logo}
                </InlineError>
              ) : null}
            </div>
            <div className="feature-company-workspace__brand-field">
              <label className="ui-field__label" htmlFor="company-settings-banner">
                Банер
              </label>
              {bannerPreviewUrl || company.banner ? (
                <img
                  className="feature-company-workspace__brand-banner"
                  src={bannerPreviewUrl || company.banner}
                  alt={`Банер ${company.name}`}
                />
              ) : null}
              <input
                ref={bannerInputRef}
                id="company-settings-banner"
                className="feature-company-workspace__brand-file-input"
                type="file"
                accept="image/*"
                disabled={saving || archiveBusy || writesSuspended}
                aria-invalid={fieldErrors.banner ? "true" : undefined}
                aria-describedby={
                  fieldErrors.banner ? "company-settings-banner-error" : undefined
                }
                onChange={onBannerInputChange}
              />
              <div className="feature-company-workspace__brand-actions">
                <button
                  ref={bannerTriggerRef}
                  type="button"
                  className="action-btn action-btn--ghost"
                  disabled={saving || archiveBusy || writesSuspended}
                  onClick={() => bannerInputRef.current?.click()}
                >
                  {bannerFile || company.banner
                    ? "Замінити банер"
                    : "Обрати банер"}
                </button>
              </div>
              {bannerFile ? (
                <p className="feature-company-workspace__brand-pending" role="status">
                  {pendingBrandingCopy(bannerFile, "банер")}
                </p>
              ) : null}
              {fieldErrors.banner ? (
                <InlineError id="company-settings-banner-error">
                  {fieldErrors.banner}
                </InlineError>
              ) : null}
            </div>
          </div>
          <SharedImageEditorModal
            show={Boolean(bannerEditor)}
            sourceFile={bannerEditor?.file || null}
            kind="company_banner"
            title="Редагувати банер"
            onApply={onBannerApply}
            onCancel={onBannerCancel}
            disabled={saving || archiveBusy || writesSuspended}
            restoreFocusRef={bannerTriggerRef}
          />
          {EDITABLE.map((name) => (
            <Field
              key={name}
              id={`company-settings-${name}`}
              label={FIELD_LABELS[name] || name}
              error={fieldErrors[name]}
            >
              {name === "description" ? (
                <Textarea
                  name={name}
                  value={values[name]}
                  onChange={onChange(name)}
                  rows={4}
                />
              ) : (
                <TextInput
                  name={name}
                  value={values[name]}
                  onChange={onChange(name)}
                />
              )}
            </Field>
          ))}
          <Checkbox
            id="company-settings-open"
            name="is_open_to_join"
            checked={values.is_open_to_join}
            onChange={onChange("is_open_to_join")}
            label="Відкрита для заявок на вступ"
          />
          {formError ? <InlineError>{formError}</InlineError> : null}
          <div className="feature-company-workspace__inline-actions">
            <button
              type="submit"
              className="action-btn"
              disabled={saving || writesSuspended || brandingEditorActive}
            >
              {saving ? "Збереження…" : "Зберегти"}
            </button>
            {showArchive ? (
              <button
                type="button"
                className="action-btn action-btn--danger"
                onClick={onArchive}
                disabled={archiveBusy || writesSuspended || brandingEditorActive}
              >
                {archiveBusy ? "Архівування…" : "Архівувати компанію"}
              </button>
            ) : null}
          </div>
        </form>
      </div>
    </section>
  );
}

export default CompanySettingsContent;
